import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ENGAGEMENT_LABEL, getProject } from '../data/projects';
import { getProjectArt } from '../data/projectArt';
import { cardSurface, darkCardSurface } from './bandedStyles';
import BandDisclosure from './BandDisclosure';

/**
 * The three pieces only the AI money page (/services/ai-solutions, round 30) needs, built on the
 * banded service-page surfaces: the offer cards, the source badge and the safety band, plus the job
 * panel that sits inside a ServiceTabs panel.
 *
 * Everything the banded pages already have (ServiceTabs, Card, StepList, ChipList, the FAQ, the
 * sticky bar, the dark-card recipe and the band disclosure) is reused, not copied.
 *
 * Plain markup throughout, so the text stays in the prerendered HTML exactly as written.
 */

/* ---------------------------------------------------------------- source badge */

/**
 * Whose work a project is. The page never blends these: an in-house platform is not client
 * work, and a client project that is only `contracted` (commissioned, not shown to be delivered)
 * says so. The kind comes from the catalogue entry, never from the copy around it.
 */
export type SourceKind = 'client' | 'commissioned' | 'inhouse';

export const SOURCE_LABEL: Record<SourceKind, string> = {
  client: ENGAGEMENT_LABEL.client,
  commissioned: `${ENGAGEMENT_LABEL.client} · commissioned`,
  inhouse: ENGAGEMENT_LABEL['in-house'],
};

/**
 * The badge a catalogue project earns, or null when it has not earned one: a `proposed`
 * client project is neither delivered nor commissioned, so it is not shown at all.
 */
export const sourceKindOf = (slug: string): SourceKind | null => {
  const project = getProject(slug);
  if (!project) return null;
  if (project.engagement === 'in-house') return 'inhouse';
  if (project.outcomeStatus === 'contracted') return 'commissioned';
  if (project.outcomeStatus === 'delivered' || project.outcomeStatus === 'measured') return 'client';
  return null;
};

const Badge = styled.span<{ $kind: SourceKind; $onDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 1px 8px;
  border: 1px solid var(--davinci-denim);
  border-radius: 4px;
  background: var(--white);
  color: var(--davinci-denim);
  font-size: max(13px, var(--fs-xs));
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.04em;
  line-height: 1.5;
  text-transform: uppercase;

  ${({ $kind }) =>
    $kind === 'commissioned' &&
    css`
      border-style: dashed;
    `}

  /* Our own platform: filled Midnight on light, Beach outline on dark. */
  ${({ $kind, $onDark }) =>
    $kind === 'inhouse' &&
    ($onDark
      ? css`
          border-color: var(--davinci-beach);
          background: rgba(143, 215, 229, 0.18);
          color: var(--davinci-beach);
        `
      : css`
          border-color: var(--davinci-midnight);
          background: var(--davinci-midnight);
          color: var(--white);
        `)}
`;

interface SourceBadgeProps {
  slug: string;
  onDark?: boolean;
}

export const SourceBadge: React.FC<SourceBadgeProps> = ({ slug, onDark }) => {
  const kind = sourceKindOf(slug);
  if (!kind) return null;
  return (
    <Badge $kind={kind} $onDark={onDark} data-source={kind}>
      {SOURCE_LABEL[kind]}
    </Badge>
  );
};

/* ---------------------------------------------------------------- offer cards */

export interface Offer {
  icon: React.ReactNode;
  title: string;
  text: string;
  first: React.ReactNode;
}

const OfferList = styled.ul`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }
`;

const OfferItem = styled.li`
  ${cardSurface}
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  column-gap: 14px;
  align-items: start;
  margin: 0;
  padding: 18px;
  color: var(--text-primary);

  @media (min-width: 900px) {
    display: block;
    padding: 24px;
  }

  .icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(11, 152, 206, 0.1);
    color: var(--davinci-blue-fill);

    svg {
      width: 24px;
      height: 24px;
    }

    @media (min-width: 900px) {
      margin-bottom: 14px;
    }
  }

  h3 {
    margin: 0;
    font-size: var(--fs-lg);
    font-weight: var(--font-weight-extra-bold);
    line-height: 1.2;
    color: var(--davinci-midnight);
  }

  p {
    margin: 0.4rem 0 0;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .first {
    margin-top: 0.55rem;
    font-size: max(13px, 0.88rem);
    line-height: 1.45;
  }

  .first b {
    color: var(--davinci-midnight);
  }

  a {
    color: var(--color-accent-primary-fill);
    font-weight: var(--font-weight-bold);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/** The page's three search targets (agents, automation, strategy), always visible, never behind a tab. */
export const OfferCards: React.FC<{ offers: Offer[] }> = ({ offers }) => (
  <OfferList>
    {offers.map((offer) => (
      <OfferItem key={offer.title}>
        <span className="icon" aria-hidden="true">{offer.icon}</span>
        <div>
          <h3>{offer.title}</h3>
          <p>{offer.text}</p>
          <p className="first">
            <b>First step:</b> {offer.first}
          </p>
        </div>
      </OfferItem>
    ))}
  </OfferList>
);

/* ---------------------------------------------------------------- job panel (inside a tab) */

const PanelSplit = styled.div`
  display: grid;
  gap: 20px;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 34px;
    align-items: center;
  }
`;

const PanelTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-size: var(--fs-lg);
  font-weight: var(--font-weight-extra-bold);
  line-height: 1.25;
  color: var(--davinci-midnight);
`;

const PanelCopy = styled.p`
  max-width: 70ch;
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
`;

const LinkList = styled.ul`
  display: grid;
  gap: 8px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.6rem;
    margin: 0;
    font-size: 0.92rem;
  }

  a {
    color: var(--color-accent-primary-fill);
    font-weight: var(--font-weight-bold);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const ArtLinkCard = styled(Link)`
  display: block;
  overflow: hidden;
  border-radius: 16px;
  background: var(--davinci-midnight);
  box-shadow: 0 6px 20px rgba(20, 53, 98, 0.08);
  text-decoration: none;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 26px rgba(20, 53, 98, 0.16);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }

  img {
    display: block;
    width: 100%;
    height: 150px;
    object-fit: cover;
    background: var(--davinci-denim);

    @media (min-width: 900px) {
      height: 200px;
    }
  }

  .caption {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    padding: 10px 12px 12px;
    color: var(--white);
    font-size: max(13px, 0.84rem);
    font-weight: var(--font-weight-semibold);
    line-height: 1.35;
  }
`;

export interface JobLink {
  slug: string;
  label: string;
}

interface JobPanelProps {
  title: string;
  children: React.ReactNode;
  /** Further catalogue projects for this job, each with its own badge. */
  links?: JobLink[];
  /** The painted card: a catalogue project, the caption shown on it, and its own badge. */
  art: { slug: string; caption: string };
  onProjectClick?: () => void;
}

/**
 * One tab panel of "jobs we have built". Every project link carries a SourceBadge that comes
 * from the catalogue entry (`engagement` and `outcomeStatus`), so a panel cannot present an
 * in-house platform as client work or a commissioned project as delivered.
 */
export const JobPanel: React.FC<JobPanelProps> = ({ title, children, links, art, onProjectClick }) => {
  const painting = getProjectArt(art.slug);
  return (
    <PanelSplit>
      <div>
        <PanelTitle>{title}</PanelTitle>
        <PanelCopy>{children}</PanelCopy>
        {links && links.length > 0 && (
          <LinkList>
            {links.map((link) => (
              <li key={link.slug}>
                <Link to={`/projects/${link.slug}`} onClick={onProjectClick}>{link.label}</Link>
                <SourceBadge slug={link.slug} />
              </li>
            ))}
          </LinkList>
        )}
      </div>
      <ArtLinkCard to={`/projects/${art.slug}`} onClick={onProjectClick}>
        {painting && (
          <img
            loading="lazy"
            decoding="async"
            alt=""
            width={painting.width}
            height={painting.height}
            sizes="(min-width: 900px) 280px, 100vw"
            srcSet={`${painting.src480} 480w, ${painting.src800} 800w`}
            src={painting.src480}
          />
        )}
        <span className="caption">
          {art.caption}
          <SourceBadge slug={art.slug} onDark />
        </span>
      </ArtLinkCard>
    </PanelSplit>
  );
};

/* ---------------------------------------------------------------- safety band */

export interface SafetyRule {
  icon: React.ReactNode;
  title: string;
  text: string;
}

interface SafetyBandProps {
  id: string;
  heading: string;
  stat: {
    /** A proofPoints.ts value. */
    value: string;
    /** What it counts, with its period. */
    text: string;
    /** One honest limit sentence. */
    note: string;
    /** The catalogue project the figure comes from; its badge shows under the figure. */
    slug: string;
  };
  rules: SafetyRule[];
  disclosureLabel: string;
  /** The definition panel, kept in the HTML while closed. */
  children: React.ReactNode;
}

const Band = styled.section`
  ${darkCardSurface}
  z-index: 2;

  /* The same glow as the proof band on managed IT. */
  &::before {
    background:
      radial-gradient(110% 90% at 90% -20%, rgba(11, 152, 206, 0.5), transparent 60%),
      linear-gradient(180deg, var(--davinci-midnight), rgb(16, 42, 79));
  }
`;

const BandInner = styled.div`
  padding: 22px 16px 6px;

  @media (min-width: 768px) {
    padding: 32px 36px 8px;
  }

  h2 {
    max-width: 30ch;
    margin: 0;
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-extra-bold);
    line-height: var(--lh-snug);
    letter-spacing: -0.01em;
    color: var(--white);

    @media (min-width: 768px) {
      font-size: var(--fs-2xl);
    }
  }
`;

const BandGrid = styled.div`
  display: grid;
  gap: 18px;
  margin-top: 18px;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);
    gap: 44px;
    align-items: center;
    margin-top: 24px;
  }
`;

const BigStat = styled.div`
  .value {
    display: block;
    font-size: clamp(3.2rem, 15vw, 5.4rem);
    font-weight: var(--font-weight-extra-bold);
    letter-spacing: -0.03em;
    line-height: 0.95;
    color: var(--white);
  }

  .text {
    display: block;
    max-width: 34ch;
    margin-top: 0.6rem;
    font-size: 0.92rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.88);
  }

  .note {
    display: block;
    max-width: 34ch;
    margin-top: 0.35rem;
    font-size: max(13px, 0.84rem);
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.86);
  }

  .badge {
    display: block;
    margin-top: 0.7rem;
  }
`;

const Rules = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  li {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    column-gap: 12px;
    align-items: start;
    margin: 0;
    padding: 13px 14px 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.06);
  }

  .icon {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: rgba(143, 215, 229, 0.14);
    color: var(--davinci-beach);

    svg {
      width: 18px;
      height: 18px;
    }
  }

  b {
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.98rem;
    line-height: 1.25;
    color: var(--white);
  }

  span.text {
    display: block;
    font-size: max(13px, 0.86rem);
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.88);
  }
`;

const Definition = styled.div`
  padding: 2px 0 12px;
  font-size: max(13px, var(--fs-xs));
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.86);

  strong {
    color: var(--white);
  }

  a {
    color: var(--davinci-beach);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  a:focus-visible {
    outline: 3px solid var(--davinci-beach);
    outline-offset: 2px;
  }
`;

/**
 * The page's one dark band: how an agent is run safely, with the one measured AI figure beside
 * four rules. The figure is our own help desk and says so (badge from the catalogue), never
 * client proof. The definition is a disclosure that stays in the HTML while closed.
 */
export const SafetyBand: React.FC<SafetyBandProps> = ({ id, heading, stat, rules, disclosureLabel, children }) => (
  <Band aria-labelledby={id}>
    <BandInner>
      <h2 id={id}>{heading}</h2>
      <BandGrid>
        <BigStat>
          <span className="value">
            {stat.value}
          </span>
          <span className="text">{stat.text}</span>
          <span className="note">{stat.note}</span>
          <span className="badge">
            <SourceBadge slug={stat.slug} onDark />
          </span>
        </BigStat>
        <Rules>
          {rules.map((rule) => (
            <li key={rule.title}>
              <span className="icon" aria-hidden="true">{rule.icon}</span>
              <div>
                <b>{rule.title}</b>
                <span className="text">{rule.text}</span>
              </div>
            </li>
          ))}
        </Rules>
      </BandGrid>
      <BandDisclosure label={disclosureLabel}>
        <Definition>{children}</Definition>
      </BandDisclosure>
    </BandInner>
  </Band>
);
